import { apiInstance, gql } from "@/services/httpAdapter";
import { errorLogger } from "@Common/helpers/logger";
import { SPECIAL_UNITS_LIST_FIELDS } from "./fragments";

import { SpecialUnitsRulesFragment } from "../fragments/specialUnits";

export async function createSpecialUnitsSetup(args) {
	const { locationId, commissionCycleId, motivatorType, input } = args;
	const query = gql`
		mutation createSpecialUnitsList(
			$locationId: String!
			$commissionCycleId: String!
			$specialUnitsListType: String
			$input: CreateSetupInput!
		) {
			createSpecialUnitsList(
				locationId: $locationId
				commissionCycleId: $commissionCycleId
				specialUnitsListType: $specialUnitsListType
				input: $input
			) {
				units {
					commission {
						input
						value
					}
					createdBy {
						fullName
					}
					createdAt
					_id
					description
					lastModifiedOn
					stockNumber
				}
				_id
				createdBy {
					fullName
				}
				createdAt
				lastModifiedBy {
					fullName
				}
				lastModifiedOn
				title
				unitsCount
				specialUnitsListType
				rules {
					...SpecialUnitsRules
				}
			}
		}
		${SpecialUnitsRulesFragment}
	`;

	const variables = {
		locationId,
		commissionCycleId,
		input,
		specialUnitsListType: motivatorType,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.createSpecialUnitsList;
	} catch (err) {
		errorLogger(err, { function: "addSpecialUnit", message: "Error adding special unit", variables });
		return null;
	}
}

export async function deleteSpecialUnitsSetup(args) {
	const { locationId, unitListId, commissionCycleId, specialUnitsListType } = args;
	const query = gql`
		mutation deleteSpecialUnitsList(
			$locationId: String!
			$unitListId: String!
			$specialUnitsListType: String
			$commissionCycleId: String
		) {
			deleteSpecialUnitsList(
				locationId: $locationId
				unitListId: $unitListId
				specialUnitsListType: $specialUnitsListType
				commissionCycleId: $commissionCycleId
			) {
				_id
				commissionCycleId
			}
		}
	`;

	const variables = {
		locationId,
		unitListId,
		commissionCycleId,
		specialUnitsListType,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.deleteSpecialUnitsList;
	} catch (err) {
		errorLogger(err, { function: "deleteSpecialUnits", message: "Error deleting special units", variables });
		return null;
	}
}

export async function updateSpecialUnitEffectiveDate({ locationId, listId, effectiveDateName, effectiveDateValue, specialUnitsListType }) {
	const query = gql`
		mutation updateSpecialUnitsList(
			$locationId: String!
			$unitListId: String!
			$specialUnitsListType: String
			$input: SpecialUnitsListInput!
		) {
			updateSpecialUnitsList(
				locationId: $locationId
				unitListId: $unitListId
				specialUnitsListType: $specialUnitsListType
				input: $input
			) { ${SPECIAL_UNITS_LIST_FIELDS}, rules { ...SpecialUnitsRules } }
		}
		${SpecialUnitsRulesFragment}
	`;

	const variables = {
		locationId,
		specialUnitsListType,
		input: {
			[effectiveDateName]: effectiveDateValue,
		},
		unitListId: listId,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.updateSpecialUnitsList;
	} catch (err) {
		errorLogger(err, { function: "updateSpecialUnitEffectiveDate", message: "Error updating special unit effective date", variables });
		return null;
	}
}

// Delete Special Unit Item start
function generateCommonDeleteSpecialUnitsQuery(unitsFragment) {
	return gql`
        mutation deleteSpecialUnitsItem($locationId: String!, $unitListId: String!, $specialUnitsListType: String, $unitItemIds: [String], $commissionCycleId: String) {
            deleteSpecialUnitsItem(
            locationId: $locationId
            unitListId: $unitListId
			specialUnitsListType: $specialUnitsListType
            unitItemIds: $unitItemIds
            commissionCycleId: $commissionCycleId
        ) {
				commissionCycleId
				_id
                title
                ${unitsFragment}
                createdBy {
                    fullName
                }
                createdAt
                lastModifiedBy {
                    fullName
                }
                lastModifiedOn
            }
        }
    `;
}
export async function deleteSpecialUnitItem(args) {
	const { specialUnitsListType } = args;

	if (specialUnitsListType === "Managed") {
		return deleteSpecialUnitItemManaged(args);
	} else {
		return deleteSpecialUnitItemManual(args);
	}
}
const deleteSpecialUnitItemManual = async (args) => {
	const { locationId, listId, commissionCycleId, itemIds, specialUnitsListType } = args;
	const unitsFragment = `
        units {
            _id
            commission {
                input
                value
            }
            createdBy {
                fullName
            }
            createdAt
            description
            lastModifiedOn
            stockNumber
        }
    `;
	const query = generateCommonDeleteSpecialUnitsQuery(unitsFragment);

	const variables = {
		locationId,
		specialUnitsListType,
		commissionCycleId,
		unitListId: listId,
		unitItemIds: itemIds,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.deleteSpecialUnit;
	} catch (err) {
		errorLogger(err, { function: "deleteSpecialUnitItemManual", message: "Error deleting special unit item", variables });
		return null;
	}
};
const deleteSpecialUnitItemManaged = async (args) => {
	const { locationId, listId, commissionCycleId, itemIds, specialUnitsListType } = args;
	const unitsFragment = `
		units {
			_id
			hierarchy
			isParent
			search
			criteria
			inventoryList {
				_id
				hierarchy
				isParent
				inventory {
					_id
					createdAt
					locationId
					status
					properties {
						displayName
						dataType
						value
					}
					updatedAt
					averageAge
				}
			}
			commission {
				input
				value
			}
			createdBy {
				fullName
			}
			createdAt
			lastModifiedOn
		}
    `;
	const query = generateCommonDeleteSpecialUnitsQuery(unitsFragment);

	const variables = {
		locationId,
		specialUnitsListType,
		commissionCycleId,
		unitListId: listId,
		unitItemIds: itemIds,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.deleteSpecialUnitsItem;
	} catch (err) {
		errorLogger(err, { function: "deleteSpecialUnitItemManaged", message: "Error deleting special unit item", variables });
		return null;
	}
};

export async function updateSpecialUnitTitle(arg) {
	const { locationId, listId, value, specialUnitsListType } = arg;
	const query = gql`
		mutation updateSpecialUnitsList(
			$locationId: String!
			$unitListId: String!
			$specialUnitsListType: String
			$input: SpecialUnitsListInput!
		) {
			updateSpecialUnitsList(
				locationId: $locationId
				unitListId: $unitListId
				specialUnitsListType: $specialUnitsListType
				input: $input
			) { ${SPECIAL_UNITS_LIST_FIELDS}, rules { ...SpecialUnitsRules } }
		}
		${SpecialUnitsRulesFragment}
	`;

	const variables = {
		locationId,
		specialUnitsListType,
		unitListId: listId,
		input: {
			title: value,
		},
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.updateSpecialUnitsList;
	} catch (err) {
		errorLogger(err, { function: "updateSpecialUnitTitle", message: "Error updating special unit title", variables });
		return null;
	}
}

export async function addNewSpecialUnitToList(args) {
	const { specialUnitsListType } = args;
	if (specialUnitsListType === "Managed") {
		return addNewSpecialUnitItemManaged(args);
	} else if (specialUnitsListType === "Manual") {
		return addNewSpecialUnitItemManual(args);
	}
	return false;
}

function generateCommonSpecialUnitsQuery(unitsFragment) {
	return gql`
        mutation createSpecialUnitsItem(
            $locationId: String!
            $unitListId: String!
            $specialUnitsListType: String
            $commissionCycleId: String
            $input: SpecialUnitsItemInput!
        ) {
            createSpecialUnitsItem(
                locationId: $locationId
                unitListId: $unitListId
                specialUnitsListType: $specialUnitsListType
                commissionCycleId: $commissionCycleId
                input: $input
            ) {
                commissionCycleId
                _id
                title
                unitsCount
                ${unitsFragment}
                createdBy {
                    fullName
                }
                createdAt
                lastModifiedBy {
                    fullName
                }
                lastModifiedOn
                rules {
					...SpecialUnitsRules
				}
            }
        }
		${SpecialUnitsRulesFragment}
    `;
}

export async function addNewSpecialUnitItemManaged(args) {
	const { locationId, listId, commissionCycleId, row, specialUnitsListType } = args;
	const unitsFragment = `
		units {
			_id
			hierarchy
			isParent
			search
			criteria
			inventoryList {
				_id
				hierarchy
				isParent
				inventory {
					_id
					createdAt
					locationId
					status
					properties {
						displayName
						dataType
						value
					}
					updatedAt
					averageAge
				}
			}
			commission {
				input
				value
			}
			createdBy {
				fullName
			}
			createdAt
			lastModifiedOn
		}
    `;
	const query = generateCommonSpecialUnitsQuery(unitsFragment);

	const variables = {
		locationId,
		commissionCycleId,
		specialUnitsListType,
		unitListId: listId,
		input: {
			managedFields: {
				search: row.search,
				criteria: row.criteria,
				query: JSON.stringify(row.query),
				unitsIds: row.rowsIds,
				actions: row.actions,
			},
			commonFields: {
				_id: row?._id || null,
				commission: row.commission,
				createdAt: row.createdAt,
				createdBy: row.createdBy,
				lastModifiedBy: row.lastModifiedBy,
				lastModifiedOn: row.lastModifiedOn,
			},
		},
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.createSpecialUnitsItem;
	} catch (err) {
		errorLogger(err, { function: "addNewSpecialUnitToList", message: "Error adding new special unit to list", variables });
		return null;
	}
}

export async function addNewSpecialUnitItemManual(args) {
	const { locationId, listId, commissionCycleId, row, specialUnitsListType } = args;
	const unitsFragment = `
        units {
            _id
            commission {
                input
                value
            }
            createdBy {
                fullName
            }
            createdAt
            description
            lastModifiedOn
            stockNumber
        }
    `;
	const query = generateCommonSpecialUnitsQuery(unitsFragment);

	const variables = {
		locationId,
		commissionCycleId,
		specialUnitsListType,
		unitListId: listId,
		input: {
			manualFields: {
				stockNumber: row.stockNumber,
				description: row.description,
			},
			commonFields: {
				_id: row?._id || null,
				commission: row.commission,
				createdAt: row.createdAt,
				createdBy: row.createdBy,
				lastModifiedBy: row.lastModifiedBy,
				lastModifiedOn: row.lastModifiedOn,
			},
		},
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.createSpecialUnitsItem;
	} catch (error) {
		errorLogger(error, { function: "addNewSpecialUnitToList", message: "Error adding new special unit to list", variables });
		throw new Error(error);
	}
}

export async function updateSpecialUnistRule(arg) {
	const { locationId, listId, input, specialUnitsListType } = arg;
	const query = gql`
		mutation updateSpecialUnitsList(
			$locationId: String!
			$unitListId: String!
			$specialUnitsListType: String
			$input: SpecialUnitsListInput!
		) {
			updateSpecialUnitsList(
				locationId: $locationId
				unitListId: $unitListId
				specialUnitsListType: $specialUnitsListType
				input: $input
			) { ${SPECIAL_UNITS_LIST_FIELDS}, rules { ...SpecialUnitsRules } }
		}
		${SpecialUnitsRulesFragment}
	`;

	const variables = {
		locationId,
		input,
		specialUnitsListType,
		unitListId: listId,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.updateSpecialUnitsList;
	} catch (err) {
		errorLogger(err, { function: "updateSpecialUnistRule", message: "Error updating special unit rule", variables });
		return null;
	}
}

/******************************************* Reynolds Related *******************************************/
export async function updateReynoldsInventoryMapping(locationId, inputMapping) {
	const query = gql`
		mutation updateReynoldsInventoryMapping($locationId: String!, $input: ReynoldsInventoryMappingObjectInput!) {
			updateReynoldsInventoryMapping(locationId: $locationId, input: $input) {
				_id
				locationId
				integrations {
					reynoldsInventoryMapping {
						new {
							_id
							name
							mappedName
							isEditable
							isActive
							isCustom
						}
						preOwned {
							_id
							name
							mappedName
							isEditable
							isActive
							isCustom
						}
					}
				}
			}
		}
	`;

	const variables = {
		locationId,
		input: inputMapping,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.updateReynoldsInventoryMapping;
	} catch (err) {
		errorLogger(err, { function: "updateReynoldsInventoryMapping", message: "Error updating reynolds inventory mapping", variables });
		return null;
	}
}
